import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'
import { MOCK_FILAMENTS, MOCK_SPOOLS, mockCrud, loadFromStorage, saveToStorage } from '@/services/mockData'
import { TIER_FEATURES } from '@/services/stripe'
import { getNextTierName } from '@/composables/useTierLimits'
import type { Filament, Spool, FilamentFormData, SpoolFormData, FilamentMaterial } from '@/types'

const STORAGE_KEY = 'filaments'
const SPOOL_STORAGE_KEY = 'spools'

/** Default low stock threshold in grams when no per-filament threshold is set */
export const DEFAULT_LOW_STOCK_THRESHOLD = 350

/** Generate a unique filament code: phq_filament_XXXXXXXXXX (10 random alphanumeric chars) */
function generateFilamentCode(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let code = 'phq_filament_'
  for (let i = 0; i < 10; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return code
}

/** Generate a unique spool code: phq_spool_XXXXXXXXXX (10 random alphanumeric chars) */
function generateSpoolCode(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let code = 'phq_spool_'
  for (let i = 0; i < 10; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return code
}

export const useFilamentStore = defineStore('filament', () => {
  // State
  const filaments = ref<Filament[]>([])
  const spools = ref<Spool[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const _loaded = ref(false)
  const _contextKey = ref<string>('personal')

  // ==========================================
  // SPOOL GETTERS
  // ==========================================

  const getSpoolsByFilamentId = (filamentId: string): Spool[] => {
    return spools.value.filter(s => s.filament_id === filamentId)
  }

  const getActiveSpoolsByFilamentId = (filamentId: string): Spool[] => {
    return spools.value.filter(s => s.filament_id === filamentId && s.is_active)
  }

  const getSpoolById = (id: string): Spool | undefined => {
    return spools.value.find(s => s.id === id)
  }

  // ==========================================
  // AGGREGATE SYNC
  // ==========================================

  const syncFilamentAggregates = () => {
    // Replace filaments array to ensure Vue reactivity triggers
    // (in-place property mutations on array elements may not trigger
    // computed properties that depend on the array reference)
    filaments.value = filaments.value.map(filament => {
      const filamentSpools = getSpoolsByFilamentId(filament.id)
      if (filamentSpools.length > 0) {
        return {
          ...filament,
          weight_total: filamentSpools.reduce((sum, s) => sum + s.weight_total, 0),
          weight_remaining: filamentSpools.reduce((sum, s) => sum + s.weight_remaining, 0),
          spool_weight: filamentSpools[0]?.spool_weight || 0,
          cost: filamentSpools.reduce((sum, s) => sum + s.cost, 0),
        }
      }
      return filament
    })
  }

  // ==========================================
  // FILAMENT GETTERS
  // ==========================================

  const activeFilaments = computed(() =>
    filaments.value.filter(f => f.is_active)
  )

  const lowStockFilaments = computed(() =>
    filaments.value.filter(f => {
      if (!f.is_active || f.weight_total <= 0) return false
      const threshold = f.low_stock_threshold ?? DEFAULT_LOW_STOCK_THRESHOLD
      return f.weight_remaining < threshold
    })
  )

  const filamentsByMaterial = computed(() => {
    const grouped: Record<FilamentMaterial, Filament[]> = {} as Record<FilamentMaterial, Filament[]>
    filaments.value.forEach(f => {
      if (!grouped[f.material]) {
        grouped[f.material] = []
      }
      grouped[f.material].push(f)
    })
    return grouped
  })

  const totalInventoryValue = computed(() =>
    spools.value.reduce((sum, s) => {
      if (s.weight_total === 0) return sum
      const remainingRatio = s.weight_remaining / s.weight_total
      return sum + (s.cost * remainingRatio)
    }, 0)
  )

  const totalWeight = computed(() =>
    filaments.value.reduce((sum, f) => sum + f.weight_remaining, 0)
  )

  const getById = (id: string) =>
    filaments.value.find(f => f.id === id)

  /** Look up a filament by its unique phq_filament_XXXXXXXXXX code */
  const getByFilamentCode = (code: string): Filament | undefined =>
    filaments.value.find(f => f.filament_code === code)

  /** Look up a filament by its UPC barcode */
  const getByUpc = (upc: string): Filament | undefined =>
    filaments.value.find(f => f.upc === upc)

  /** Look up a spool by its unique phq_spool_XXXXXXXXXX code */
  const getSpoolByCode = (code: string): Spool | undefined =>
    spools.value.find(s => s.spool_code === code)

  // ==========================================
  // SPOOL ACTIONS
  // ==========================================

  const fetchSpools = async (teamId?: string) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return

    try {
      if (authStore.isDemoMode) {
        spools.value = loadFromStorage(SPOOL_STORAGE_KEY, MOCK_SPOOLS)
      } else {
        const { supabase } = await import('@/services/supabase')
        let query = supabase
          .from('spools')
          .select('*')
        if (teamId) {
          query = query.eq('team_id', teamId)
        } else {
          query = query.is('team_id', null).eq('user_id', authStore.userId)
        }
        const { data, error: fetchError } = await query
          .order('created_at', { ascending: true })

        if (fetchError) throw fetchError
        spools.value = data as Spool[]
      }
    } catch (err) {
      console.error('Error fetching spools:', err)
    }
  }

  const createSpool = async (filamentId: string, spoolData: Partial<SpoolFormData>, teamId?: string) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: false, error: 'Not authenticated' }

    const existingSpools = getSpoolsByFilamentId(filamentId)
    const label = spoolData.label || `Spool ${existingSpools.length + 1}`
    const spool_code = generateSpoolCode()

    try {
      if (authStore.isDemoMode) {
        const newSpool = mockCrud.create(SPOOL_STORAGE_KEY, {
          ...spoolData,
          filament_id: filamentId,
          spool_code,
          label,
          user_id: authStore.userId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        } as Spool, spools.value)
        spools.value.push(newSpool)
        syncFilamentAggregates()
        return { success: true, data: newSpool }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: insertError } = await supabase
          .from('spools')
          .insert({
            ...spoolData,
            filament_id: filamentId,
            spool_code,
            label,
            user_id: authStore.userId,
            ...(teamId ? { team_id: teamId } : {}),
          })
          .select()
          .single()

        if (insertError) throw insertError
        spools.value.push(data as Spool)
        syncFilamentAggregates()
        return { success: true, data }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create spool'
      return { success: false, error: msg }
    }
  }

  const updateSpool = async (id: string, updates: Partial<SpoolFormData>) => {
    const authStore = useAuthStore()

    try {
      if (authStore.isDemoMode) {
        const updated = mockCrud.update(SPOOL_STORAGE_KEY, id, updates, spools.value)
        if (updated) {
          const index = spools.value.findIndex(s => s.id === id)
          if (index !== -1) spools.value[index] = updated
        }
        syncFilamentAggregates()
        return { success: true, data: updated }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: updateError } = await supabase
          .from('spools')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('id', id)
          .select()
          .single()

        if (updateError) throw updateError

        const index = spools.value.findIndex(s => s.id === id)
        if (index !== -1) spools.value[index] = data as Spool

        syncFilamentAggregates()
        return { success: true, data }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update spool'
      return { success: false, error: msg }
    }
  }

  const deleteSpool = async (id: string) => {
    const authStore = useAuthStore()
    const spool = getSpoolById(id)
    if (!spool) return { success: false, error: 'Spool not found' }

    try {
      if (authStore.isDemoMode) {
        mockCrud.delete(SPOOL_STORAGE_KEY, id, spools.value)
        spools.value = spools.value.filter(s => s.id !== id)
        syncFilamentAggregates()
        return { success: true }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { error: deleteError } = await supabase
          .from('spools')
          .delete()
          .eq('id', id)

        if (deleteError) throw deleteError
        spools.value = spools.value.filter(s => s.id !== id)
        syncFilamentAggregates()
        return { success: true }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete spool'
      return { success: false, error: msg }
    }
  }

  // ==========================================
  // FILAMENT ACTIONS
  // ==========================================

  const fetchFilaments = async (force = false, { silent = false, teamId }: { silent?: boolean; teamId?: string } = {}) => {
    const authStore = useAuthStore()
    if (!authStore.userId) {
      console.warn('[filamentStore] fetchFilaments called without userId')
      return
    }

    const newContext = teamId || 'personal'
    if (newContext !== _contextKey.value) {
      _loaded.value = false
      _contextKey.value = newContext
    }

    if (_loaded.value && !force) return

    if (!silent) {
      loading.value = true
      error.value = null
    }

    try {
      if (authStore.isDemoMode) {
        await new Promise(resolve => setTimeout(resolve, 300))
        filaments.value = loadFromStorage(STORAGE_KEY, MOCK_FILAMENTS)
      } else {
        const { supabase } = await import('@/services/supabase')
        let query = supabase
          .from('filaments')
          .select('*')
        if (teamId) {
          query = query.eq('team_id', teamId)
        } else {
          query = query.is('team_id', null).eq('user_id', authStore.userId)
        }
        const { data, error: fetchError } = await query
          .order('created_at', { ascending: false })

        if (fetchError) throw fetchError
        filaments.value = data as Filament[]
      }

      // Also fetch spools and sync aggregates
      await fetchSpools(teamId)
      syncFilamentAggregates()
      _loaded.value = true
    } catch (err) {
      if (!silent) error.value = err instanceof Error ? err.message : 'Failed to fetch filaments'
      console.error('Error fetching filaments:', err)
    } finally {
      if (!silent) loading.value = false
    }
  }

  const createFilament = async (filamentData: FilamentFormData, teamId?: string, spoolOptions?: { sealed?: boolean }) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: false, error: 'Not authenticated' }

    // Tier limit check (skip for team context)
    if (!teamId) {
      const tierConfig = TIER_FEATURES[authStore.subscriptionTier]
      const limit = tierConfig.filaments
      if (limit !== -1 && filaments.value.length >= limit) {
        const nextTier = getNextTierName(authStore.subscriptionTier)
        const upgradeHint = nextTier ? ` Upgrade to ${nextTier} for more.` : ''
        return { success: false, error: `You've reached the ${tierConfig.name} plan limit of ${limit} filaments.${upgradeHint}` }
      }
    }

    loading.value = true
    error.value = null

    try {
      let newFilament: Filament
      const filament_code = generateFilamentCode()
      if (authStore.isDemoMode) {
        newFilament = mockCrud.create(STORAGE_KEY, {
          ...filamentData,
          filament_code,
          user_id: authStore.userId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        } as Filament, filaments.value)
        filaments.value.unshift(newFilament)
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: insertError } = await supabase
          .from('filaments')
          .insert({ ...filamentData, filament_code, user_id: authStore.userId, ...(teamId ? { team_id: teamId } : {}) })
          .select()
          .single()

        if (insertError) throw insertError
        newFilament = data as Filament
        filaments.value.unshift(newFilament)
      }

      // Auto-create first spool from the filament form's weight/cost data
      await createSpool(newFilament.id, {
        filament_id: newFilament.id,
        label: 'Spool 1',
        weight_total: filamentData.weight_total,
        weight_remaining: filamentData.weight_remaining,
        spool_weight: filamentData.spool_weight,
        cost: filamentData.cost,
        purchase_date: filamentData.purchase_date || undefined,
        opened_date: filamentData.opened_date || undefined,
        is_active: true,
        sealed: spoolOptions?.sealed === true,
        storage_location: filamentData.storage_location || undefined,
      }, teamId)

      syncFilamentAggregates()
      return { success: true, data: newFilament }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create filament'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const updateFilament = async (id: string, updates: Partial<FilamentFormData>) => {
    const authStore = useAuthStore()
    loading.value = true
    error.value = null

    try {
      if (authStore.isDemoMode) {
        const updated = mockCrud.update(STORAGE_KEY, id, updates, filaments.value)
        if (updated) {
          const index = filaments.value.findIndex(f => f.id === id)
          if (index !== -1) filaments.value[index] = updated
        }
        syncFilamentAggregates()
        return { success: true, data: updated }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: updateError } = await supabase
          .from('filaments')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('id', id)
          .select()
          .single()

        if (updateError) throw updateError

        const index = filaments.value.findIndex(f => f.id === id)
        if (index !== -1) filaments.value[index] = data as Filament

        // Re-sync aggregates since the DB response has stale weight/cost
        // values — the real totals come from summing spools
        syncFilamentAggregates()

        return { success: true, data }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update filament'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const deleteFilament = async (id: string) => {
    const authStore = useAuthStore()
    loading.value = true
    error.value = null

    try {
      if (authStore.isDemoMode) {
        // Also delete all spools for this filament
        const filamentSpools = getSpoolsByFilamentId(id)
        for (const s of filamentSpools) {
          mockCrud.delete(SPOOL_STORAGE_KEY, s.id, spools.value)
        }
        spools.value = spools.value.filter(s => s.filament_id !== id)

        mockCrud.delete(STORAGE_KEY, id, filaments.value)
        filaments.value = filaments.value.filter(f => f.id !== id)
        return { success: true }
      } else {
        const { supabase } = await import('@/services/supabase')
        // Spools cascade-delete via FK in the migration
        const { error: deleteError } = await supabase
          .from('filaments')
          .delete()
          .eq('id', id)

        if (deleteError) throw deleteError
        spools.value = spools.value.filter(s => s.filament_id !== id)
        filaments.value = filaments.value.filter(f => f.id !== id)
        return { success: true }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete filament'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  // ==========================================
  // FILAMENT USAGE (spool-aware)
  // ==========================================

  const useFilament = async (filamentId: string, spoolId: string, amountUsed: number, printId?: string) => {
    const authStore = useAuthStore()
    const spool = getSpoolById(spoolId)
    if (!spool) return { success: false, error: 'Spool not found' }

    const newRemaining = Math.max(0, spool.weight_remaining - amountUsed)
    const result = await updateSpool(spoolId, { weight_remaining: newRemaining })

    // Write a filament_usage record for history tracking
    if (result.success && printId) {
      try {
        if (authStore.isDemoMode) {
          const usages = loadFromStorage<any[]>('filament_usage', [])
          usages.push({
            id: `fu-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            user_id: authStore.userId,
            filament_id: filamentId,
            spool_id: spoolId,
            print_id: printId,
            amount_used: amountUsed,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          saveToStorage('filament_usage', usages)
        } else {
          const { supabase } = await import('@/services/supabase')
          await supabase.from('filament_usage').insert({
            user_id: authStore.userId,
            filament_id: filamentId,
            spool_id: spoolId,
            print_id: printId,
            amount_used: amountUsed,
          })
        }
      } catch (err) {
        console.error('Failed to write filament_usage record:', err)
      }
    }

    return result
  }

  const refillSpool = async (spoolId: string, addedWeight: number) => {
    const spool = getSpoolById(spoolId)
    if (!spool) return { success: false, error: 'Spool not found' }

    const newRemaining = Math.min(spool.weight_total, spool.weight_remaining + addedWeight)
    return updateSpool(spoolId, { weight_remaining: newRemaining })
  }

  const toggleActive = async (id: string) => {
    const filament = getById(id)
    if (!filament) return { success: false, error: 'Filament not found' }
    return updateFilament(id, { is_active: !filament.is_active })
  }

  const clearData = () => {
    filaments.value = []
    spools.value = []
    _loaded.value = false
  }

  return {
    // State
    filaments,
    spools,
    loading,
    error,
    _loaded,
    _contextKey,
    // Filament getters
    activeFilaments,
    lowStockFilaments,
    filamentsByMaterial,
    totalInventoryValue,
    totalWeight,
    getById,
    getByFilamentCode,
    getByUpc,
    // Spool getters
    getSpoolsByFilamentId,
    getActiveSpoolsByFilamentId,
    getSpoolById,
    getSpoolByCode,
    // Filament actions
    fetchFilaments,
    createFilament,
    updateFilament,
    deleteFilament,
    toggleActive,
    // Spool actions
    fetchSpools,
    createSpool,
    updateSpool,
    deleteSpool,
    // Usage actions
    useFilament,
    refillSpool,
    syncFilamentAggregates,
    clearData,
  }
})
